import { createWork4MeSkill } from '@/api/skills-api';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { toast } from 'sonner';
import { Work4MeSkillForm } from './Work4MeSkillForm';

interface Work4MeSkillCreateProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated?: (skillId: number) => void;
}

export function Work4MeSkillCreate({ open, onOpenChange, onCreated }: Work4MeSkillCreateProps) {
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [body, setBody] = useState('');
  const [isEnabled, setIsEnabled] = useState(true);

  const createMutation = useMutation({
    mutationFn: () => createWork4MeSkill({
      name: name.trim(),
      description: description.trim() ? description.trim() : null,
      body: body.trim(),
      isEnabled,
    }),
    onSuccess: async (created) => {
      toast.success(`Created "${created.name}".`);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['process-skill-options'] }),
        queryClient.invalidateQueries({ queryKey: ['work4me-skill', created.id] }),
      ]);
      onCreated?.(created.id);
      onOpenChange(false);
      setName('');
      setDescription('');
      setBody('');
      setIsEnabled(true);
    },
    onError: (error: unknown) => {
      const message = error instanceof Error ? error.message : 'Failed to create skill.';
      toast.error(message);
    },
  });

  return (
    <Sheet
      open={open}
      onOpenChange={(nextOpen) => {
        onOpenChange(nextOpen);
        if (!nextOpen) {
          setName('');
          setDescription('');
          setBody('');
          setIsEnabled(true);
        }
      }}
    >
      <SheetContent className="w-screen sm:w-[640px] flex flex-col px-0">
        <SheetHeader className="px-4">
          <SheetTitle>Create Skill</SheetTitle>
          <SheetDescription>Create a member-scoped Work4Me skill for this workspace.</SheetDescription>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto">
          <Work4MeSkillForm
            name={name}
            description={description}
            body={body}
            isEnabled={isEnabled}
            isDirty={true}
            isSaving={createMutation.isPending}
            onNameChange={setName}
            onDescriptionChange={setDescription}
            onBodyChange={setBody}
            onIsEnabledChange={setIsEnabled}
            onSave={() => createMutation.mutate()}
            submitLabel="Create Skill"
            submittingLabel="Creating..."
            editableHint="New skill configuration is saved when created."
          />
        </div>

        <SheetFooter className="px-4">
          <div className="w-full">
            <Separator className="mb-4" />
            <Button variant="outline" onClick={() => onOpenChange(false)} className="w-full">
              Close
            </Button>
          </div>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}

